import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tabs, Tab, Snackbar, Alert } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import platformAdminService from '../../services/platformAdminService';
import CompaniesPanel from './CompaniesPanel';
import PartnersPanel from './PartnersPanel';
import UsersPanel from './UsersPanel';
import RolesPanel from './RolesPanel';
import LLMCatalogPanel from './LLMCatalogPanel';
import AuditPanel from './AuditPanel';
import PlatformMcpPanel from './PlatformMcpPanel';
import UsageAnalyticsPanel from '../shared/UsageAnalytics/UsageAnalyticsPanel';
import { buildPartnerIndex, companyLabel } from '../../utils/tenancy';
import { USAGE_COPY } from '../../constants/partnerCopy';
import styles from './PlatformAdmin.module.scss';

// Each tab is gated by its platform-scope permission key (spec §3.2). A full
// platform admin holds the wildcard, so all tabs show; a narrower platform-staff
// role would see only the sections it's granted.
const TABS = [
  { value: 'companies', label: 'Companies', perm: PERMISSIONS.PLATFORM_COMPANIES },
  { value: 'partners', label: 'Partners', perm: PERMISSIONS.PLATFORM_PARTNERS },
  { value: 'users', label: 'Users', perm: PERMISSIONS.PLATFORM_USERS },
  { value: 'roles', label: 'Roles', perm: PERMISSIONS.PLATFORM_ROLES },
  { value: 'llm', label: 'LLM Catalog', perm: PERMISSIONS.PLATFORM_LLM_CATALOG },
  { value: 'usage', label: 'LLM Usage', perm: PERMISSIONS.PLATFORM_USAGE_ANALYTICS },
  { value: 'audit', label: 'Audit Log', perm: PERMISSIONS.PLATFORM_AUDIT },
  // No platform-scope key: the shared Quadroid server is platform-admin only.
  { value: 'platformMcp', label: 'Platform MCP', adminOnly: true },
];

/**
 * Platform Admin console (spec §4). A single tabbed surface covering Companies +
 * entitlements, cross-company Users, Roles (both tiers), and the Audit log.
 *
 * Auth: the logged-in platform admin's x-api-key (no separate admin-key gate).
 * The route is gated on is_platform_admin in Phase 1, so reaching this component
 * already implies authorization.
 */
const PlatformAdmin = () => {
  const { isDarkMode } = useTheme();
  const { has, isPlatformAdmin } = usePermissions();
  const [tab, setTab] = useState('companies');
  const [catalog, setCatalog] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Stable identity: panels use `notify` in useCallback/useEffect deps, so an
  // unstable notify would re-trigger their data-loaders on every toast/re-render.
  const notify = useCallback(
    (message, severity = 'info') => setSnackbar({ open: true, message, severity }),
    []
  );

  const tabs = useMemo(
    () => TABS.filter((t) => (t.adminOnly ? isPlatformAdmin : has(t.perm))),
    [has, isPlatformAdmin]
  );
  // Render the permitted tab even before the sync effect runs.
  const activeTab = tabs.some((t) => t.value === tab) ? tab : tabs[0]?.value;

  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.value === tab)) {
      setTab(tabs[0].value);
    }
  }, [tabs, tab]);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  // Partner index (Partner tier guide §4.7): names the Partner column/filters and
  // maps each partner's hidden home company to "<Partner> · direct users" across
  // Companies, Users, Roles, Usage and Audit. Loaded once; the Partners tab
  // reloads it after changes. A failure only degrades labels, so it's silent.
  const canPartners = has(PERMISSIONS.PLATFORM_PARTNERS);
  const [partners, setPartners] = useState([]);
  const loadPartners = useCallback(async () => {
    if (!canPartners) {
      setPartners([]);
      return;
    }
    try {
      const data = await platformAdminService.listPartners({ limit: 200 });
      setPartners(data?.partners || (Array.isArray(data) ? data : []));
    } catch (err) {
      console.warn('Failed to load partners', err);
    }
  }, [canPartners]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const partnerIndex = useMemo(() => buildPartnerIndex(partners), [partners]);
  const partnerFilterList = partners.length ? partners : null;
  const companyLabelFn = useCallback(
    (id, name) => companyLabel(id, name, { partnerIndex }),
    [partnerIndex]
  );
  // §4.9 suggests partner,model as the default view once partners exist.
  const usageDefaultGroupBy = useMemo(
    () => (partners.length ? ['partner', 'model'] : undefined),
    [partners.length]
  );

  // Permission catalog is shared by the Companies (entitlements) and Roles panels.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await platformAdminService.getPermissionCatalog();
        if (!cancelled) setCatalog(Array.isArray(data?.permissions) ? data.permissions : []);
      } catch (err) {
        if (!cancelled) notify(err?.message || 'Failed to load permission catalog.', 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.layout}>
        <Box className={styles.inner}>
          <Box className={styles.header}>
            <Box className={styles.titleSection}>
              <Typography variant="h4" component="h1">Platform Admin</Typography>
              <Typography variant="body2">
                Manage companies, partners and their allowed areas, users across companies, roles, and the audit trail.
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setTab(v)}
            className={styles.consoleTabs}
            TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} className={styles.tab} />
            ))}
          </Tabs>

          {activeTab === 'companies' && (
            <CompaniesPanel notify={notify} catalog={catalog} partnerIndex={partnerIndex} />
          )}
          {activeTab === 'partners' && (
            <PartnersPanel
              notify={notify}
              catalog={catalog}
              partnerIndex={partnerIndex}
              onPartnersChanged={loadPartners}
            />
          )}
          {activeTab === 'users' && (
            <UsersPanel notify={notify} partnerIndex={partnerIndex} partners={partnerFilterList} />
          )}
          {activeTab === 'roles' && <RolesPanel notify={notify} catalog={catalog} partnerIndex={partnerIndex} />}
          {activeTab === 'llm' && <LLMCatalogPanel notify={notify} />}
          {activeTab === 'usage' && (
            <UsageAnalyticsPanel
              basePath="/platform/usage"
              scope="platform"
              notify={notify}
              defaultGroupBy={usageDefaultGroupBy}
              partners={partnerFilterList}
              companyLabelFn={companyLabelFn}
              dateTooltip={partnerFilterList ? USAGE_COPY.attribution : null}
            />
          )}
          {activeTab === 'audit' && (
            <AuditPanel notify={notify} partners={partnerFilterList} companyLabelFn={companyLabelFn} />
          )}
          {activeTab === 'platformMcp' && (
            <PlatformMcpPanel notify={notify} partners={partnerFilterList} companyLabelFn={companyLabelFn} />
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default PlatformAdmin;
